import * as Sentry from '@sentry/gatsby'

import { pageAuthAgeBucket, secondsSincePageAuthenticated } from './src/lib/auth/page-auth-age'
import { getPageState, pageStateChanges$ } from './src/utils/pageState'
import {
  injectedRuntimeEvidence,
  stackOverflowAttribution,
  translationEvidence,
} from './src/utils/stackOverflow'

// Track when the page last changed lifecycle state so error events can show whether they fired right
// after a background/restore transition — e.g. an iOS tab suspended for a while then resumed into an
// expired auth session or replaced cart. That is a recurring condition in the sc-142309 /
// DFH-CORP-SITES-JSG evidence, although it is not itself the render-loop feedback edge.
let pageStateChangedAtMs: number | undefined
if (typeof window !== 'undefined') {
  pageStateChanges$.subscribe(() => {
    pageStateChangedAtMs = Date.now()
  })
}

Sentry.init({
  // __SENTRY_DSN__ defined in webpack from SENTRY_DSN by gatsby-node.js
  dsn: __SENTRY_DSN__,
  integrations: [
    ...(process.env.NODE_ENV !== 'development' ? [Sentry.browserTracingIntegration()] : []),
    Sentry.extraErrorDataIntegration({
      // increase depth to capture response headers and body (for fetch error)
      depth: 4,
    }),
    // User Feedback widget. `autoInject: false` keeps it hidden until we open it programmatically
    // from the render-loop recovery UI (RenderLoopRecoveryFallback); see Auth0WithFallbackProvider.
    // Bundled (not the async/CDN variant) so it works reliably from an already-broken page state and
    // submits over the DSN transport (connect-src https://*.sentry.io is already allowed by CSP).
    ...(typeof window !== 'undefined'
      ? [
          Sentry.feedbackIntegration({
            autoInject: false,
            showBranding: false,
            colorScheme: 'light',
            formTitle: 'Tell us what happened',
            submitButtonLabel: 'Send report',
            messagePlaceholder: 'What were you doing when the page stopped working?',
            // Brand the widget (colors here; the Gotham font is set via the `#sentry-feedback`
            // CSS variable in global.css since fontFamily is not a themeLight option). Brand tokens
            // come from packages/ui preset: navy #0B2240, orange #E64B37.
            themeLight: {
              background: '#FFFFFF',
              foreground: '#0B2240',
              accentBackground: '#0B2240',
              accentForeground: '#FFFFFF',
              errorColor: '#E64B37',
              outline: '2px solid rgba(11, 34, 64, 0.6)',
            },
          }),
        ]
      : []),
  ],
  // __SENTRY_ENVIRONMENT__ defined in webpack from SENTRY_ENVIRONMENT by gatsby-node.js
  ...(!!__SENTRY_ENVIRONMENT__ && {
    environment: __SENTRY_ENVIRONMENT__,
  }),
  // __SENTRY_RELEASE__ defined in webpack from SENTRY_RELEASE by gatsby-node.js
  release: __SENTRY_RELEASE__,
  sampleRate: 1.0,
  sendDefaultPii: true,
  tracesSampleRate: 0.1,
  beforeSend(event) {
    if (typeof document === 'undefined') {
      return event
    }
    // Stamp the page lifecycle state at capture time. `seconds_since_change` near 0 with `state:
    // active` points at an error fired immediately on tab restore/foreground.
    const state = getPageState()
    event.contexts = {
      ...event.contexts,
      'Page State': {
        state,
        seconds_since_change:
          pageStateChangedAtMs === undefined
            ? undefined
            : Math.round((Date.now() - pageStateChangedAtMs) / 1000),
      },
    }
    // Also a tag, because only tags are indexed: the context shows the state on one event, the tag
    // is what lets us ask how much of an issue fired right after a restore. Stamped per event here
    // rather than via setTag, so it can never go stale on the scope.
    event.tags = { ...event.tags, 'page.state': state }

    // How long ago THIS PAGE LOAD became authenticated — not the age of the Auth0 session, which a
    // reload would restore while this resets. Stamped here for the same reason as page.state:
    // TokenContextProvider's Sentry effect only runs when auth state changes, so an age recorded
    // there reports seconds for an error fired hours into an idle tab, which is the case
    // sc-142309 / DFH-CORP-SITES-JSG is about.
    const pageAuthAge = pageAuthAgeBucket()
    if (pageAuthAge) {
      event.tags['auth.page_authenticated_age'] = pageAuthAge
      event.contexts['Auth State'] = {
        ...event.contexts['Auth State'],
        secondsSincePageAuthenticated: secondsSincePageAuthenticated(),
      }
    }

    // On every event, not just overflows, so injected overflows can be compared against the baseline
    // translate rate of other errors from the same browsers (sc-146245).
    const translation = translationEvidence(document)
    event.tags = { ...event.tags, 'stack.translated': translation.translated ? 'yes' : 'no' }

    // `RangeError: Maximum call stack size exceeded` groups by stack, and on WebKit the frames of
    // runtime-injected script are attributed to the page URL, so that population minted a fresh issue
    // per page: 116 issues over 90 days, none individually big enough to triage, hiding 1,683 events
    // across 328 users (sc-145026). Pin the fingerprint for that population so its real rate lands on
    // one graph.
    //
    // Deliberately not pinned for the rest: traces carrying real filenames already group correctly —
    // DFH-CORP-SITES-HRD held one group for six months and 47 events — so there is nothing to
    // un-fragment there, and pinning them would merge the next genuine recursion in our own code into
    // an ongoing issue, where it raises no new-issue alert and no release-health breach.
    const overflow = stackOverflowAttribution(event)
    if (overflow) {
      if (overflow.origin === 'injected') {
        event.fingerprint = ['maximum-call-stack-size-exceeded', overflow.origin]
      }
      // Tagged, not just contextualised, because only tags are indexed and the whole point is being
      // able to ask which injected origin is responsible without shipping another release. The top
      // frame's filename stays out of the tags and in the context: for a first-party trace it is a
      // per-release hashed bundle URL, so as a tag it would grow the value set without bound.
      // Stamped on every origin, not just 'injected', so the injected population can be compared
      // against the rest: if it is always 'present' and the others are not, Chrome for iOS's own
      // scripts are the culprit (sc-146245).
      const runtime = injectedRuntimeEvidence(window)
      event.tags = {
        ...event.tags,
        'stack.overflow_origin': overflow.origin,
        'stack.frame_origins': overflow.frameOrigins.join(' '),
        'stack.gcrweb': runtime.gcrweb ? 'present' : 'absent',
      }
      event.contexts['Stack Overflow'] = {
        top_filename: overflow.topFilename,
        frame_origins: [...overflow.frameOrigins],
        gcrweb_modules: [...runtime.modules],
        gcrweb_module_count: runtime.moduleCount,
        document_lang: translation.lang,
      }
    }
    return event
  },
})
